import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send, X } from 'lucide-react';
import { formatIstTime } from '@gbv/utils';
import { Button, Spinner, Textarea, cn, useToast } from '@gbv/ui';
import { api, ApiRequestError } from '../lib/api';

/**
 * The client's floating chat.
 *
 * One conversation — them and the GoBeeVir team — reachable from any screen,
 * so a question that occurs while reading a report can be asked without
 * leaving it. The same thread as /messages; this is a second door onto it, not
 * a second inbox.
 */

interface Message {
  id: string;
  body: string;
  senderType: 'CLIENT' | 'EMPLOYEE';
  senderName: string;
  createdAt: string;
}

export function FloatingChat() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['client-messages'],
    queryFn: () => api.get<{ messages: Message[]; unread: number }>('/messages'),
    // Polled while shut so the badge is right without the panel being open.
    refetchInterval: open ? 20_000 : 60_000,
  });

  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [open, thread.data]);

  // Opening it is reading it.
  useEffect(() => {
    if (!open) return;
    void api.post('/messages/read').then(() => {
      void queryClient.invalidateQueries({ queryKey: ['client-messages'] });
      void queryClient.invalidateQueries({ queryKey: ['client-dashboard'] });
    });
  }, [open, queryClient]);

  const send = useMutation({
    mutationFn: () => api.post('/messages', { body: draft.trim() }),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['client-messages'] });
    },
    onError: (err) =>
      toast.error(
        'Message not sent',
        err instanceof ApiRequestError ? err.message : 'Try again in a moment.',
      ),
  });

  const messages = thread.data?.messages ?? [];
  const unread = thread.data?.unread ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full shadow-overlay transition',
          open ? 'bg-ink text-white' : 'bg-vir text-white hover:bg-vir-dark',
        )}
        aria-label={open ? 'Close messages' : 'Message your team'}
      >
        {open ? <X size={19} /> : <MessageSquare size={19} />}
        {!open && unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-20 right-5 z-40 flex h-[520px] max-h-[calc(100vh-7rem)] w-[360px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-card border border-line bg-white shadow-overlay">
          <div className="border-b border-line px-4 py-3">
            <p className="text-sm font-semibold text-ink">Your GoBeeVir team</p>
            <p className="text-[11px] text-ink-muted">
              We usually reply the same working day.
            </p>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
            {thread.isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <MessageSquare size={20} className="text-ink-soft" />
                <p className="mt-2 text-sm text-ink">No messages yet</p>
                <p className="mt-1 text-xs text-ink-muted">
                  Ask us anything about your account, your leads, or an invoice.
                </p>
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.senderType === 'CLIENT';
                return (
                  <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                    <div
                      className={cn(
                        'max-w-[85%] rounded-card px-3 py-2',
                        mine ? 'bg-vir text-white' : 'bg-surface-page text-ink',
                      )}
                    >
                      {!mine && (
                        <p className="mb-0.5 text-[10px] font-semibold text-ink-soft">
                          {m.senderName}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
                      <p
                        className={cn(
                          'mt-0.5 text-[9px]',
                          mine ? 'text-white/60' : 'text-ink-soft',
                        )}
                      >
                        {formatIstTime(new Date(m.createdAt))}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-line p-2">
            <Textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (draft.trim()) send.mutate();
                }
              }}
              placeholder="Type your message…"
              aria-label="Message your team"
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <span className="text-[10px] text-ink-soft">Enter to send</span>
              <Button
                size="sm"
                leftIcon={<Send size={12} />}
                onClick={() => send.mutate()}
                isLoading={send.isPending}
                disabled={draft.trim().length === 0}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
